/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme as useSuperAppTheme } from '@/contexts/ThemeProvider';
import { useBookstoreModule } from '@/pages/Bookstore/contexts/BookstoreModuleContext';

/**
 * Adaptador de tema para componentes portados da bookstore (front-end/sites/bookstore).
 *
 * Selecione o produto correto (biblioteca | banca | audiobook) com base no contexto
 * de módulo ativo (BookstoreModuleProvider) e converte o shape do produto para o que
 * os componentes da bookstore esperam:
 *   - product.defaultProduct.isBookstore  (camelCase)
 *   - product.metadata.hasMagazine.value  (case-insensitive lookups)
 *   - bookstoreTypes: string[]
 */
export function useTheme() {
  const superTheme = useSuperAppTheme() as any;
  const moduleContext = useBookstoreModule();
  const [searchParams] = useSearchParams();
  const requestedProductId = Number(searchParams.get('produto')) || undefined;

  const selectedRaw = useMemo(() => {
    const modules = superTheme.bookstoreModules || {};

    // Quem veio de /produtos manda o acervo escolhido em ?produto=<id> — clubes
    // com vários produtos de leitura (Educa, Profissional, ...) dependem disso.
    if (requestedProductId) {
      const requested = (superTheme.productBenefits || []).find(
        (p: any) => p?.id === requestedProductId && p?.default_product?.is_bookstore
      );
      if (requested) return requested;
    }

    if (moduleContext?.moduleType === 'banca') return modules.banca?.product;
    if (moduleContext?.moduleType === 'audiobook') return modules.audiobook?.product;
    if (moduleContext?.moduleType === 'biblioteca') return modules.biblioteca?.product;
    // fallback: biblioteca > banca > qualquer um (legado)
    return modules.biblioteca?.product || modules.banca?.product || superTheme.bookstoreProduct;
  }, [
    requestedProductId,
    moduleContext,
    superTheme.bookstoreModules,
    superTheme.bookstoreProduct,
    superTheme.productBenefits,
  ]);

  const product = useMemo(() => normalizeProduct(selectedRaw), [selectedRaw]);

  const bookstoreTypes = useMemo(() => {
    const types: string[] = [];
    if (superTheme.productData?.hasBookstore) types.push('book');
    if (superTheme.productData?.hasMagazine) types.push('magazine');
    if (superTheme.productData?.hasAudioBook) types.push('audiobook');
    return types;
  }, [superTheme.productData]);

  return {
    ...superTheme,
    product,
    bookstoreTypes,
    showBestSellers: !!superTheme.productData?.hasBestSellers,
    bookstoreLabels: superTheme.bookstoreLabels || {
      book: 'livro',
      books: 'livros',
      magazine: 'revista',
      magazines: 'revistas',
      audiobook: 'audiobook',
      audiobooks: 'audiobooks',
      prefixedPlural: 'obras',
    },
    digitalMagazinesModule: !!superTheme.digitalMagazinesModule,
    recurringBook: superTheme.recurringBook ?? true,
    uraModuleCellphone: superTheme.cellphone || superTheme.telephone || '',
  };
}

function normalizeProduct(raw: any) {
  if (!raw) return undefined;

  // Metadata: mantém as keys originais e duplica em lowercase
  // (componentes da bookstore consultam ora camelCase ora lowercase).
  const metadata: Record<string, any> = {};
  Object.entries(raw.metadata || {}).forEach(([key, value]) => {
    metadata[key] = value;
    metadata[key.toLowerCase()] = value;
  });

  // default_product: gerar versão camelCase em defaultProduct
  const dpRaw = raw.default_product || raw.defaultProduct || {};
  const defaultProduct = {
    ...dpRaw,
    isBookstore: !!dpRaw.is_bookstore,
    isSuperapp: !!dpRaw.is_superapp,
    createLanding: !!dpRaw.create_landing,
  };

  return {
    ...raw,
    id: raw.id,
    metadata,
    defaultProduct,
    default_product: dpRaw, // preserva original para quem consulta snake_case
  };
}
